import PropTypes from 'prop-types';
import React, { memo } from 'react';

import Popup from '../helpers/Popup';
import FormGroup from '../helpers/FormGroup';
import BaseInput from '../helpers/Input';
import PrettyError from '../decorators/PrettyError';
import DisplayError from '../decorators/DisplayError';
import { Spinner } from '../helpers/Spinner';
import Button from '../helpers/Button';
import useRequiredFieldErrors from '../../hooks/useRequiredFieldErrors';

const Input = PrettyError(DisplayError(BaseInput));

/**
 * Mailing Template Upload popup.
 *
 * @param props {Object}.
 * @param props.onUpload {Function} To handle clicking on create button.
 * @param props.onClose {Function} To handle closing popup.
 * @param props.onChange {Function} To handle text changing.
 * @param props.onChoose {Function} To handle file changing.
 * @param props.name {String} File name.
 * @param props.disabledUpload {Boolean} If set to true, will disable create button.
 */
const UploadPopup = props => {
  const {
    isUploading = false,
    disabledUpload = false,
    hasErrors = false,
    mimeType = null,
    onUpload,
    onClose,
    onChange,
    onChoose,
    name,
    setFormElement,
  } = props;
  const { errors: requiredErrors, formProps } = useRequiredFieldErrors();
  const header = 'Upload a Mailing Template';
  const footer = (
    <>
      <Button
        key="create"
        className="btn-primary"
        disabled={disabledUpload || isUploading || hasErrors}
        onClick={onUpload}
      >
        Upload {isUploading && <Spinner size="small" inline />}
      </Button>
      <Button key="close" onClick={onClose}>
        Close
      </Button>
    </>
  );

  return (
    <Popup footer={footer} header={header} id="uploadPopup">
      <form ref={setFormElement} encType="multipart/form-data" {...formProps}>
        <div className="row">
          <FormGroup className="col-xs-6">
            <Input
              canDisplayError={Boolean(requiredErrors.name)}
              className="input-sm"
              error={requiredErrors.name}
              id="k-template-name"
              label="Template Name"
              name="name"
              onChange={onChange}
              placeholder="Template Name"
              value={name}
              autoFocus
              required
              showLabel
            />
          </FormGroup>
          <FormGroup className="col-xs-6">
            <Input
              canDisplayError={Boolean(requiredErrors.file)}
              className="input-sm uploader"
              error={requiredErrors.file}
              id="k-template-file"
              label="File"
              name="file"
              placeholder=""
              type="file"
              {...(mimeType === null ? {} : { accept: mimeType })}
              onChange={onChoose}
              required
              showLabel
            />
          </FormGroup>
        </div>
      </form>
    </Popup>
  );
};

UploadPopup.propTypes = {
  disabledUpload: PropTypes.bool,
  isUploading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onChoose: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  setFormElement: PropTypes.func.isRequired,
};

export default memo(UploadPopup);
